import styled, { css, keyframes } from 'styled-components';
import type { BaseColor } from '~/theme/System/tokens';

export type ContainerVariant = 'insert' | 'choose' | 'preview';

const pulse = keyframes`
  from {
    transform: scale(1);
    opacity: 0.9;
  }
  to {
    transform: scale(1.2);
    opacity: 1;
  }
`;

const variantStyles: Record<
  ContainerVariant,
  {
    color: BaseColor;
    backgroundColor: BaseColor;
    border: {
      color: BaseColor;
      style: string;
    };
  }
> = {
  choose: {
    color: 'white',
    backgroundColor: 'primary',
    border: {
      color: 'primary',
      style: 'solid',
    },
  },
  preview: {
    color: 'white',
    backgroundColor: 'secondary',
    border: {
      color: 'white',
      style: 'dashed',
    },
  },
  insert: {
    color: 'primary',
    backgroundColor: 'white',
    border: {
      color: 'primary',
      style: 'dashed',
    },
  },
};

export const Container = styled.button<{
  $variant: ContainerVariant;
  $interactive?: boolean;
}>(
  ({ theme, $variant, $interactive = false }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    user-select: none;
    cursor: ${$interactive ? 'pointer' : 'default'};
    pointer-events: ${$interactive ? 'auto' : 'none'};
    color: ${theme.color(variantStyles[$variant].color)};
    background-color: ${theme.color(variantStyles[$variant].backgroundColor)};
    border-width: 2px;
    border-style: ${variantStyles[$variant].border.style};
    border-color: ${theme.color(variantStyles[$variant].border.color)};
    border-radius: ${theme.getTokens().border.radius.s};
    padding: ${theme.space('s')} ${theme.space('base')};
    gap: ${theme.space('xs')};
    font-weight: ${theme.fontWeight('semiBold')};
    transition: all 0.3s ease-out;
    animation: ${$variant === 'preview'
      ? 'none'
      : css`
          ${pulse} 1s ease-in alternate infinite
        `};

    ${$interactive &&
    $variant !== 'preview' &&
    css`
      &:hover {
        transform: scale(1.3);
      }
    `}
  `,
);
